import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { useAppSelector, useIsDemoUser } from '@/hooks/redux';
import { useAutoPushPromptSettled, usePushStatus } from '@/hooks';
import { enablePushNotifications } from '@/lib/pushNotifications';
import { isFcmPromptSnoozed, snoozeFcmPrompt } from '@/lib/fcmDevice';

const SHOW_DELAY_MS = 1000;
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Fallback prompt for when the automatic browser permission prompt after login
 * (see autoRequestPushPermission) got no answer — dismissed, shown quietly by
 * Chrome, or suppressed by Firefox/Safari, which only prompt from a click. Its
 * "Allow" button is that click. "Not now" snoozes it and the automatic prompt.
 */
export function PushNotificationPrompt() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isDemoUser = useIsDemoUser();
  const status = usePushStatus();
  const autoPromptSettled = useAutoPushPromptSettled();
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const eligible = isAuthenticated && !isDemoUser && autoPromptSettled && status === 'default';

  useEffect(() => {
    if (!eligible || isFcmPromptSnoozed()) {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [eligible]);

  if (!visible) return null;

  const dismiss = () => {
    snoozeFcmPrompt(SNOOZE_MS);
    setVisible(false);
  };

  const allow = async () => {
    setRequesting(true);
    const result = await enablePushNotifications();
    setRequesting(false);
    // Closing the browser prompt without choosing leaves permission at "default";
    // snooze so we don't keep nagging.
    if (result === 'default') snoozeFcmPrompt(SNOOZE_MS);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="push-prompt-title"
      className="fixed inset-x-3 bottom-[calc(63.5px+12px)] z-[996] animate-in fade-in slide-in-from-bottom-4 duration-300 md:inset-x-auto md:bottom-4 md:right-4 md:w-[360px]"
    >
      <div className="relative flex gap-3 rounded-[10px] border-l-4 border-[#FEC733] bg-white p-4 pr-9 text-black shadow-[0px_4px_16px_0px_#00000033] dark:bg-darkPanelBg dark:text-white dark:shadow-[0px_4px_16px_0px_#00000099]">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FEC733]/20 text-[#E0A800] dark:bg-[#FEC733]/15 dark:text-[#FEC733]">
          <BellRing size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <p id="push-prompt-title" className="text-sm font-semibold leading-5">
            Get alerts when your matches go live
          </p>
          <p className="mt-0.5 text-xs leading-4 text-black/70 dark:text-white/80">
            Turn on notifications for live match and bet updates.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={allow}
              disabled={requesting}
              className="h-8 rounded-[6px] bg-[#FEC733] px-4 text-xs font-semibold text-black transition hover:bg-[#f5bb1a] disabled:opacity-60"
            >
              {requesting ? 'Enabling…' : 'Allow'}
            </button>
            <button
              type="button"
              onClick={dismiss}
              disabled={requesting}
              className="h-8 rounded-[6px] px-3 text-xs font-semibold text-black/60 transition hover:bg-black/5 hover:text-black dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white"
            >
              Not now
            </button>
          </div>
        </div>

        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="absolute right-2 top-2 rounded p-0.5 text-black/40 hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
